import { getUserWishlist } from '../containers/FavoritePage/FavoritePage.duck';
import {
    CURRENT_USER_INITIAL_LOAD_COMPLETE,
    fetchUnreadMessages,
    updateUserProfileInfo,
} from '../ducks/user.duck';
import config from '../config';
import { removeUserWishlistCache, updateCurrentUserLocationField } from '../util/localStorage';
import { queryOwnListings } from '../containers/ManageListingsPage/ManageListingsPage.duck';
import { queryRiderListing } from '../ducks/riderListings.duck';
import { stringifyToMarketplaceISO } from '../util/dates';
import { isNativeIOS } from '../util/ios';
import { checkCurrentUserUpdates } from '../ducks/Routing.duck';
import { userIsNotAvailable } from '../util/user';
import { LOGIN_COMPLETE, LOGOUT_SUCCESS } from '../ducks/Auth.duck';
import { fetchActiveTransactions } from '../containers/ProfilePage/ProfilePage.duck';
import {
    RIDER_AVAILABILITY_CONFIRMED,
    RIDER_AVAILABILITY_NOT_AVAILABLE,
    RIDER_AVAILABILITY_NOT_CONFIRMED,
} from '../marketplace-custom-config';

const { userTypeHorseowner, userTypeRider } = config;

const checkUserUpdatesOnFirstRender = () => dispatch => {
    if (isNativeIOS()) {
        /** Handle cases e.g. when a user buys credits, and the app is reloaded */
        dispatch(checkCurrentUserUpdates());
    }
};

const loadCurrentUserInitialData = ({ getState, dispatch }) => {
    dispatch(fetchUnreadMessages());
    dispatch(getUserWishlist());
    dispatch(checkUserUpdatesOnFirstRender());
    dispatch(fetchActiveTransactions());

    const { currentUser } = getState().user;

    if (userIsNotAvailable(currentUser)) return;

    const {
        attributes: {
            deleted,
            emailVerified,
            profile: {
                publicData: {
                    userType,
                    emailVerified: emailVerifiedPD,
                    country,
                    availabilityStatus,
                    availabilityFirstTimeConfirmed,
                },
            },
        },
    } = currentUser;

    const isHorseowner = userType === userTypeHorseowner;
    const isRider = userType === userTypeRider;
    const isNotAvailable = availabilityStatus === RIDER_AVAILABILITY_NOT_AVAILABLE;
    const isNotConfirmed = availabilityStatus === RIDER_AVAILABILITY_NOT_CONFIRMED;

    if (isHorseowner) {
        dispatch(queryOwnListings({}));
    }

    /**
     * a rider's own rider-listing is used for matching on SearchPage and
     * ListingPage (see riderListings.duck.js:selectOwnRiderListings); it is
     * fetched once here, as soon as the current user is known.
     */
    if (isRider) {
        dispatch(queryRiderListing(currentUser.id.uuid));
    }

    if (!deleted) {
        /** if there is a country field, update localStorage value */
        if (country) {
            updateCurrentUserLocationField('countrySelected', country);
        }

        const reflectEmailVerificationStatus = !!emailVerified && !emailVerifiedPD;
        const params = {
            publicData: {
                lastVisitedAt: stringifyToMarketplaceISO(new Date()),
            },
        };

        if (isRider && !isNotAvailable && availabilityFirstTimeConfirmed) {
            params.publicData.availabilityStatus = RIDER_AVAILABILITY_CONFIRMED;
            params.publicData.availabilityUpdatedAt = stringifyToMarketplaceISO(new Date());
            params.publicData.availabilityUpdatedAtMethod = 'Log-in';
            if (isNotConfirmed) {
                params.publicData.availabilityStatusPrevious = RIDER_AVAILABILITY_NOT_CONFIRMED;
            }
        }

        if (reflectEmailVerificationStatus) {
            params.publicData = {
                emailVerified: true,
            };
        }

        dispatch(updateUserProfileInfo(params));
    }
};

export const reactOnCurrentUserDataLoad = () => ({ getState, dispatch }) => next => action => {
    const { type } = action;
    /**
     * CURRENT_USER_INITIAL_LOAD_COMPLETE fires only on a full page load;
     * an email/password login happens in-app, so LOGIN_COMPLETE covers it
     * (the current user is already fetched by then, see Auth.duck's login)
     */
    if (type === CURRENT_USER_INITIAL_LOAD_COMPLETE || type === LOGIN_COMPLETE) {
        loadCurrentUserInitialData({ getState, dispatch });
    }

    if (type === LOGOUT_SUCCESS) {
        /** the cache is per user; do not keep one user's favourites in a shared browser */
        removeUserWishlistCache();
    }

    next(action);
};
